import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';



import { makeStyles } from 'tss-react/mui';
import {
  ApiButton,
  Box,
  Button,
  Grid,
  MUIDataTable,
  Pill,
  SearchBar,
  SectionHeader,
  Typography,
} from 'web-library';
import {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  useDataTable,
} from 'web-library/stories/organism/DataTable/DataTable';
import { useHistory, useParams } from 'react-router-dom';
import { Radio } from '@mui/material';
import RouteKey from '../../../routes/key';
import {
  PostSearchCandidateResponse,
  SearchSubjectOptionalPapers,
  SearchSubjectResponse,
  usePostSearchCandidate,
} from '../../../api-refactored/group-subject-amendment/usePostSearchCandidate';
import { usePostCreateAmendment } from '../../../api-refactored/group-subject-amendment/usePostCreateAmendment';
import { ApplicationStatus } from '../../../api/candidate/exam/model';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    marginBottom: theme.spacing(5),
  },
  dataTable: {
    marginBottom: theme.spacing(7),
  },
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  searchInput: {
    maxWidth: '100%',
  },
  navigationButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  optionalPaper: {
     ...typographyTheme(theme, 'subtitle2'),
    color: theme.color.text.textSecondary,
  },
}));

const { amendment } = RouteKey.child;
const {
  coordinatorRegistration,
  coordinatorExamRegistrations,
  coordinatorEditRegistration,
} = RouteKey.amendment.child;

const addComma = (index: number, length: number): string => (length - 1 !== index ? ', ' : '');
const renderOptionalPaper = (optionalPapers: SearchSubjectOptionalPapers[], t: TFunction<string>) =>
  optionalPapers.map(
    (paper, index, { length }) =>
      `${t('exam->>ReviewExam.Paper')} ${paper.paperNo}${addComma(index, length)}`
  );

const isDisabled = (data: PostSearchCandidateResponse[], dataIndex: number) =>
  data[dataIndex].status === ApplicationStatus.DRAFT ||
  data[dataIndex].status === ApplicationStatus.PENDING_PAYMENT;

const useTableColumns = (
  data: PostSearchCandidateResponse[],
  classes: Record<string,string>,
  t: TFunction
): MUIDataTableColumnDef[] => [
    {
      name: 'candidateId',
      label: t('amendment->>GroupSubjectSelectCandidate.UinFinPassport'),
      options: {
        sort: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isDisabled(data, dataIndex) ? classes.textDisabled : ''}
          >
            {data[dataIndex].candidateId}
          </Typography>
        ),
      },
    },
    {
      name: 'name',
      label: t('amendment->>GroupSubjectSelectCandidate.CandidateName'),
      options: {
        sort: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isDisabled(data, dataIndex) ? classes.textDisabled : ''}
          >
            {data[dataIndex].name}
          </Typography>
        ),
      },
    },
    {
      name: 'examLevels',
      label: t('amendment->>GroupSubjectSelectCandidate.ExamLevel'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={isDisabled(data, dataIndex) ? classes.textDisabled : ''}>
            {data[dataIndex].examLevels.map((exam: string, examIndex: number) => (
              <Typography variant="body2" key={`key-${exam}`}>
                {exam}
                {examIndex < data[dataIndex].examLevels.length - 1 && <>,</>}
              </Typography>
            ))}
          </Box>
        ),
      },
    },
    {
      name: 'examSeries',
      label: t('amendment->>GroupSubjectSelectCandidate.ExamSeries'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography className={isDisabled(data, dataIndex) ? classes.textDisabled : ''}>
            {data[dataIndex].examSeries.join(', ')}
          </Typography>
        ),
      },
    },
    {
      name: 'subjects',
      label: t('amendment->>GroupSubjectSelectCandidate.Subjects'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={isDisabled(data, dataIndex) ? classes.textDisabled : ''}>
            {data[dataIndex].subjects.map((item: SearchSubjectResponse) => (
              <>
                <Typography variant="body2">
                  {item.subjectName} ({item.subjectCode})
                </Typography>
                {item.optionalPapers.length > 0 && (
                  <Typography variant="body2" className={classes.optionalPaper}>
                    + {t('exam->>ReviewExam.Optional Papers')}:{'\u00A0'}
                    {renderOptionalPaper(item.optionalPapers, t)}
                  </Typography>
                )}
              </>
            ))}
          </Box>
        ),
      },
    },
    {
      name: 'status',
      label: t('amendment->>GroupSubjectSelectCandidate.Status'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (isDisabled(data, dataIndex)) {
            return <Pill label={data[dataIndex].status} variant="info" />;
          }

          return <></>;
        },
      },
    },
  ];

const CustomCheckbox = ({ props, classes }: { props: {}; classes: Record<string,string> }) => (
  <Radio className={classes.radioButton} size="medium" name="chkCandidate" {...props} />
);

const SelectCandidate = () => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const history = useHistory();
  const { examTypeCode } = useParams<{ examTypeCode: string }>();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 10,
  });

  const tableColumns = useTableColumns(state.data, classes, t);

  const {
    mutate: createSubjectAmendmentMutate,
    status: createSubjectAmendmentStatus,
  } = usePostCreateAmendment({
    onSuccess: (res) => {
      history.push(
        `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorEditRegistration.route}/${res.uuid}`
      );
    },
    onError: (err) => {
      if (err.response?.data?.code === '1604') {
        setErrorMessage(
          t('amendment->>GroupSubjectSelectCandidate.CandidateRegistrationIsProcessing')
        );
      }
      if (err.response?.data?.code === '5011') {
        setErrorMessage(err.response?.data?.message || '');
      }
    },
  });

  const { mutate: searchCandidateMutate } = usePostSearchCandidate({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    searchCandidateMutate({
      body: { q: state.search, examType: examTypeCode },
      apiParams: {
        order: state.order,
        sort: state.sortColumn,
        page: state.page,
        pageSize: state.size,
      },
    });
  }, [
    state.search,
    state.order,
    state.sortColumn,
    state.page,
    state.size,
    searchCandidateMutate,
    examTypeCode,
  ]);

  const customOptions: MUIDataTableOptions = {
    ...tOptions,
    selectableRows: 'single',
    selectToolbarPlacement: 'none',
    pagination: true,
    filter: false,
    sort: true,
    isRowSelectable: (dataIndex: number) => !isDisabled(state.data, dataIndex),
  };

  const selectCandidateHandler = () => {
    setErrorMessage('');
    if (Object.keys(state.rowsSelected).length === 0) {
      setErrorMessage(t('amendment->>GroupSubjectSelectCandidate.SelectACandidate'));
    } else {
      const selectedCandidatesList: string[] = Object.keys(state.rowsSelected).map(
        (s) => state.rowsSelected[s].uuid
      );

      createSubjectAmendmentMutate({ uuid: selectedCandidatesList[0] });
    }
  };

  return (
    <>
      <SectionHeader title={t('amendment->>GroupSubjectAmendment.SelectCandidate')} />
      <Box className={classes.searchContainer}>
        <SearchBar
          className={classes.searchInput}
          placeholder={t('amendment->>GroupSubjectSelectCandidate.SearchByCandidateInfo')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr,
            });
          }}
        />
      </Box>
      <Box className={classes.dataTable}>
        <MUIDataTable
          // @ts-ignore
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          options={customOptions}
          rowsPerPageOptions={[10, 20, 25, 30, 50]}
          components={{
            Checkbox: (props: any) => CustomCheckbox({ props, classes }),
          }}
        />
      </Box>
      {errorMessage && (
        <Grid xs={12} md={8}>
          <Typography className={classes.errorText} variant="body2">
            {errorMessage}
          </Typography>
        </Grid>
      )}

      <Box className={classes.navigationButtonContainer}>
        <Grid xs={12} direction="row" wrap="nowrap" container>
          <Button
            onClick={() =>
              history.push(
                `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorRegistration.route}/${examTypeCode}`
              )
            }
            className={classes.backButton}
            variant="outlined"
            id="button-back"
          >
            {t('amendment->>GroupSubjectSelectCandidate.Back')}
          </Button>
          <ApiButton
            onClick={selectCandidateHandler}
            variant="contained"
            status={createSubjectAmendmentStatus}
            id="button-edit-register-subject"
          >
            {t('amendment->>GroupSubjectSelectCandidate.EditRegisteredSubjects')}
          </ApiButton>
        </Grid>
      </Box>
    </>
  );
};

export default SelectCandidate;
